/* Phase-2 data sync: the workspace lives in D1. Studio accounts read/write the
   whole workspace; clients read/write only their project. localStorage stays as
   an offline cache. Last-write-wins with a version guard + dirty flag so polling
   never clobbers an in-flight local edit. */

import { applyRemote, getState, setPushHook } from "./store";
import type { Session } from "./auth";

type Mode = "studio" | "client" | null;
let mode: Mode = null;
let clientCode: string | null = null;
let lastVersion = 0;
let dirty = false;
let pushTimer: any = null;
let pollTimer: any = null;
let status: SyncStatus = "offline";
const statusSubs = new Set<(s: SyncStatus) => void>();

export type SyncStatus = "live" | "syncing" | "offline" | "local";
function setStatus(s: SyncStatus) { status = s; statusSubs.forEach((f) => f(s)); }
export function onSyncStatus(fn: (s: SyncStatus) => void) { statusSubs.add(fn); return () => { statusSubs.delete(fn); }; }
export function getSyncStatus() { return status; }

async function jget(url: string): Promise<any | null> {
  try { const r = await fetch(url, { credentials: "same-origin" }); if (!r.ok) return null; return await r.json(); } catch { return null; }
}
async function jput(url: string, body: any): Promise<any | null> {
  try { const r = await fetch(url, { method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); if (!r.ok) return null; return await r.json(); } catch { return null; }
}

export async function startSync(session: Session | null) {
  stopSync();
  if (!session) { setStatus("offline"); return; }
  if (session.role === "client") { mode = "client"; clientCode = session.code ?? null; }
  else { mode = "studio"; }
  setPushHook(schedulePush);
  await hydrate();
  pollTimer = setInterval(pull, 12000);
  window.addEventListener("focus", pull);
}
export function stopSync() {
  setPushHook(null);
  if (pollTimer) clearInterval(pollTimer);
  if (pushTimer) clearTimeout(pushTimer);
  window.removeEventListener("focus", pull);
  mode = null; clientCode = null; lastVersion = 0; dirty = false;
}

async function hydrate() {
  setStatus("syncing");
  if (mode === "studio") {
    const d = await jget("/api/state");
    if (!d) { setStatus("local"); return; }
    if (d.state) { applyRemote(d.state); lastVersion = d.version; setStatus("live"); }
    else { await push(); } // empty server → seed it from the local (seeded) state
  } else if (mode === "client" && clientCode) {
    const d = await jget(`/api/portal/${clientCode}`);
    if (!d) { setStatus("local"); return; }
    if (d.project) { applyRemote({ ...getState(), projects: [d.project], ...(d.rateCard ? { rateCard: d.rateCard } : {}) }); lastVersion = d.version; setStatus("live"); }
    else setStatus("local");
  }
}

async function pull() {
  if (!mode || dirty) return;
  if (mode === "studio") {
    const d = await jget("/api/state");
    if (d?.state && d.version > lastVersion) { applyRemote(d.state); lastVersion = d.version; }
    if (d) setStatus("live");
  } else if (mode === "client" && clientCode) {
    const d = await jget(`/api/portal/${clientCode}`);
    if (d?.project && d.version > lastVersion) { applyRemote({ ...getState(), projects: [d.project], ...(d.rateCard ? { rateCard: d.rateCard } : {}) }); lastVersion = d.version; }
    if (d) setStatus("live");
  }
}

function schedulePush() {
  if (!mode) return;
  dirty = true; setStatus("syncing");
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(push, 700);
}
async function push() {
  if (!mode) return;
  if (mode === "studio") {
    const d = await jput("/api/state", { state: getState() });
    if (d?.version) { lastVersion = d.version; setStatus("live"); } else setStatus("local");
  } else if (mode === "client" && clientCode) {
    const proj = getState().projects[0];
    if (proj) { const d = await jput(`/api/portal/${clientCode}`, { project: proj }); if (d?.version) { lastVersion = d.version; setStatus("live"); } else setStatus("local"); }
  }
  dirty = false;
}

/** Upload an image blob to the server; returns a served URL, or the data URI on failure. */
export async function storeImage(dataUri: string, code?: string): Promise<string> {
  try {
    const url = "/api/img" + (code ? `?code=${encodeURIComponent(code)}` : "");
    const r = await fetch(url, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ data: dataUri }) });
    if (!r.ok) return dataUri;
    const d = (await r.json()) as { url?: string };
    return d.url || dataUri;
  } catch { return dataUri; }
}

/** Upload any file (PDF, image, doc) to R2 and get a stable /api/file/:id URL.
 *  Unlike storeImage this doesn't inline — it streams the raw bytes, so a large
 *  survey PDF never bloats the workspace JSON. Throws if storage isn't set up
 *  or the upload fails, so the caller can tell the user rather than pretend. */
export async function storeFile(file: File, code?: string): Promise<{ url: string; name: string }> {
  const q = code ? `?code=${encodeURIComponent(code)}&name=${encodeURIComponent(file.name)}` : `?name=${encodeURIComponent(file.name)}`;
  const r = await fetch("/api/file" + q, {
    method: "POST", credentials: "same-origin",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  const d = (await r.json().catch(() => ({}))) as { url?: string; name?: string; error?: string };
  if (!r.ok || !d.url) throw new Error(d.error || "Upload failed");
  return { url: d.url, name: d.name || file.name };
}

/** Same as storeFile, but reports upload progress (0..1) as the bytes go up —
 *  fetch can't observe upload progress, so this uses XHR. Lets a big drawing
 *  set show a moving bar instead of appearing stuck. */
export function storeFileWithProgress(file: File, onProgress: (fraction: number) => void, code?: string): Promise<{ url: string; name: string }> {
  const q = code ? `?code=${encodeURIComponent(code)}&name=${encodeURIComponent(file.name)}` : `?name=${encodeURIComponent(file.name)}`;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/file" + q);
    xhr.withCredentials = true;
    xhr.timeout = 8 * 60 * 1000;   // don't hang forever on a stalled connection
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let d: { url?: string; name?: string; error?: string } = {};
      try { d = JSON.parse(xhr.responseText || "{}"); } catch { /* non-JSON */ }
      if (xhr.status >= 200 && xhr.status < 300 && d.url) resolve({ url: d.url, name: d.name || file.name });
      else reject(new Error(d.error || `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed — network error"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.send(file);
  });
}

/* Cloudflare caps a single Worker request body at ~100MB, so a large drawing
   set's original can't go up in one request (that's the 413). Files past this
   threshold are uploaded to R2 in parts via the multipart endpoints. */
const MPU_THRESHOLD = 80 * 1024 * 1024;   // 80MB — comfortably under the edge limit
const MPU_PART = 40 * 1024 * 1024;        // 40MB parts (R2: uniform except last, ≥5MB)

function putPart(blob: Blob, key: string, uploadId: string, part: number, onProgress: (loaded: number) => void): Promise<{ partNumber: number; etag: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `/api/file/mpu/part?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}&part=${part}`);
    xhr.withCredentials = true;
    xhr.timeout = 5 * 60 * 1000;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
    xhr.onload = () => {
      let d: any = {}; try { d = JSON.parse(xhr.responseText || "{}"); } catch { /* */ }
      if (xhr.status >= 200 && xhr.status < 300 && d.etag) resolve({ partNumber: d.partNumber, etag: d.etag });
      else reject(new Error(d.error || `part ${part} failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error(`part ${part} — network error`));
    xhr.ontimeout = () => reject(new Error(`part ${part} timed out`));
    xhr.send(blob);
  });
}

/** Store a file, choosing single-request or multipart by size, reporting overall
 *  progress (0..1). Multipart bypasses the ~100MB single-request edge limit. */
export async function storeFileSmart(file: File, onProgress: (fraction: number) => void): Promise<{ url: string; name: string }> {
  if (file.size <= MPU_THRESHOLD) return storeFileWithProgress(file, onProgress);
  // 1) create the multipart upload
  const cr = await fetch(`/api/file/mpu?name=${encodeURIComponent(file.name)}&type=${encodeURIComponent(file.type || "application/octet-stream")}`, { method: "POST", credentials: "same-origin" });
  const meta = await cr.json().catch(() => ({})) as { key?: string; uploadId?: string; error?: string };
  if (!cr.ok || !meta.key || !meta.uploadId) throw new Error(meta.error || "couldn't start the upload");
  const { key, uploadId } = meta;
  const parts: { partNumber: number; etag: string }[] = [];
  const totalParts = Math.ceil(file.size / MPU_PART);
  let doneBytes = 0;
  try {
    for (let i = 0; i < totalParts; i++) {
      const blob = file.slice(i * MPU_PART, Math.min((i + 1) * MPU_PART, file.size));
      const r = await putPart(blob, key, uploadId, i + 1, (loaded) => onProgress(Math.min(1, (doneBytes + loaded) / file.size)));
      parts.push(r);
      doneBytes += blob.size;
      onProgress(Math.min(1, doneBytes / file.size));
    }
    const done = await fetch("/api/file/mpu/complete", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key, uploadId, parts }) });
    const d = await done.json().catch(() => ({})) as { url?: string; error?: string };
    if (!done.ok || !d.url) throw new Error(d.error || "couldn't finalise the upload");
    return { url: d.url, name: file.name };
  } catch (e) {
    // leave no dangling multipart upload behind
    void fetch("/api/file/mpu/abort", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key, uploadId }) }).catch(() => {});
    throw e;
  }
}
